import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import { listTenants } from "@/api/tenants";
import { TaskRow } from "@/components/shell/TaskRow";
import { CardSkeleton, EmptyState, LoadingRegion, Page, PageHeader } from "@/components/ui";
import {
  ErrorBox,
  RefreshFailed,
  isLoadingOrPaused,
} from "@/components/Spinner";
import { useRecentDeployments } from "@/hooks/useRecentDeployments";
import { useShellState } from "@/hooks/useShellState";
import { isActiveStatus } from "@/lib/deployments";
import type { TenantStatus } from "@/types/api";

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <div className="card">
      <div className="text-xs uppercase tracking-wide text-ink-muted">{label}</div>
      <div className="mt-1 text-3xl font-semibold text-ink">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-ink-muted">{hint}</div>}
    </div>
  );
}

export function DashboardPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const { openTask } = useShellState();

  const tenantsQuery = useQuery({
    queryKey: ["tenants", "active"],
    queryFn: () => listTenants(token),
    enabled: !!token,
  });
  const feedQuery = useRecentDeployments();

  const tenants = tenantsQuery.data?.items ?? [];
  const feed = feedQuery.data?.items ?? [];

  const byStatus = useMemo(() => {
    const counts: Partial<Record<TenantStatus, number>> = {};
    for (const t of tenants) counts[t.status] = (counts[t.status] ?? 0) + 1;
    return counts;
  }, [tenants]);

  const activeTasks = useMemo(
    () => feed.filter((d) => isActiveStatus(d.status)),
    [feed],
  );

  const isLoading = isLoadingOrPaused(tenantsQuery);
  // Only an error with nothing to show replaces the cards: the archive flow
  // invalidates this query on its way back here, and a failed refetch must not
  // blank a page whose data is in the cache (the two feeds below stay as they
  // are: a stale "active tasks" list would state a false status).
  const error = tenantsQuery.isLoadingError ? tenantsQuery.error : null;

  return (
    <Page>
      <PageHeader title="Datacenter" />

      <div className="space-y-8">
        {isLoading && (
          <LoadingRegion className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </LoadingRegion>
        )}
        {error && <ErrorBox error={error} />}
        <RefreshFailed query={tenantsQuery} />

        {!isLoading && !error && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label="Active tenants"
                value={byStatus.active ?? 0}
                hint={`${tenants.length} total (excluding archived)`}
              />
              <StatCard label="Drafts" value={byStatus.draft ?? 0} />
              <StatCard
                label="Active tasks"
                value={activeTasks.length}
                hint={activeTasks.length > 0 ? "active deployments" : "nothing in progress"}
              />
              <StatCard
                label="Recent deployments"
                value={feed.length}
                hint="Last 50, all tenants"
              />
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="card">
                <h2 className="mb-3 text-lg font-medium text-ink">
                  Active tasks
                </h2>
                {feedQuery.isError ? (
                  <ErrorBox error={feedQuery.error} />
                ) : activeTasks.length === 0 ? (
                  <EmptyState size="inline" title="No deployments in progress right now." />
                ) : (
                  <ul className="divide-y divide-line">
                    {activeTasks.map((d) => (
                      <li key={`${d.tenantId}:${d.runId}`}>
                        <TaskRow
                          deployment={d}
                          light
                          onSelect={(dep) =>
                            openTask({ tenantId: dep.tenantId, runId: dep.runId })
                          }
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="card">
                <h2 className="mb-3 text-lg font-medium text-ink">
                  Recent activity
                </h2>
                {feedQuery.isError ? (
                  <ErrorBox error={feedQuery.error} />
                ) : feed.length === 0 ? (
                  <EmptyState size="inline" title="No recent activity." />
                ) : (
                  <ul className="divide-y divide-line">
                    {feed.slice(0, 8).map((d) => (
                      <li key={`${d.tenantId}:${d.runId}`}>
                        <TaskRow
                          deployment={d}
                          light
                          onSelect={(dep) =>
                            openTask({ tenantId: dep.tenantId, runId: dep.runId })
                          }
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Page>
  );
}
